// JARVIS Dynamic Agent Factory
// For every PROVEN/ELITE/HALL_OF_FAME strategy → spawn a specialized Groq agent.
// Each agent analyzes current market from its strategy's lens.
// ALL agents run (no fixed cap), batched in groups of 5 for Groq rate limit safety.

import { apiUrl } from '../services/apiBase';
import { getAgentEligibleStrategies, detectStrategyDNA, DNA_META } from './strategyLifecycle';
import { getFullJarvisContext } from '../services/storage';

// ── Standardized agent report shape ──────────────────────────────────────────
// Every agent MUST return this exact JSON. Master Brain depends on it.
export const EMPTY_REPORT = {
  agentId:      '',       // strategy.id
  strategyName: '',       // strategy.title
  dna:          '',       // concept type
  agentWeight:  1.0,      // pre-calculated weight from strategyLifecycle
  verdict:      'WAIT',   // BUY | SELL | WAIT
  confidence:   0,        // 0-100
  winRate:      0,        // strategy's historical win rate
  totalTrades:  0,        // trades this strategy has been tested on
  entryZone:    null,     // "3320-3325" or null
  stopLoss:     null,     // price or null
  target1:      null,     // price or null
  target2:      null,     // price or null
  keyConditions:[],       // conditions this strategy sees RIGHT NOW
  missingConditions:[],   // conditions NOT met
  reason:       '',       // 1-2 sentence explanation
  riskVerdict:  'CLEAR',  // CLEAR | CAUTION | AVOID
  status:       'ok',     // ok | error
  error:        null,
};

// ── Build specialized system prompt per DNA concept ───────────────────────────

function buildAgentSystemPrompt(strategy, dna, context) {
  const { currentPrice, currentSession, activeOBs, openFVGs, recentMS, behaviors } = context;

  const baseContext = `
LIVE PRICE: ${currentPrice || 'Unknown'}
SESSION: ${currentSession?.name || 'Unknown'} (${currentSession?.quality || ''})
STRATEGY WIN RATE: ${strategy.win_rate}% over ${strategy.total_trades} trades
STRATEGY STATUS: ${strategy.status}
`;

  const dnaSpecific = {
    ORDER_BLOCK: `
ACTIVE ORDER BLOCKS:
${activeOBs?.length ? activeOBs.map(ob => `${ob.type} OB @ ${ob.bottom}–${ob.top} (${ob.timeframe})`).join('\n') : 'None logged'}
Focus on: Is price approaching a valid OB? Is the OB fresh (not mitigated)?`,

    FVG: `
OPEN FAIR VALUE GAPS:
${openFVGs?.length ? openFVGs.map(f => `${f.type} FVG @ ${f.bottom}–${f.top} (${f.timeframe})`).join('\n') : 'None logged'}
Focus on: Is an FVG nearby? Is price filling or leaving an FVG?`,

    STRUCTURE: `
RECENT MARKET STRUCTURE:
${recentMS?.length ? recentMS.slice(0, 5).map(ms => `${ms.date} ${ms.event} ${ms.direction} @ ${ms.price} (${ms.timeframe})`).join('\n') : 'None logged'}
Focus on: Has there been a BOS or CHoCH? Is structure bullish or bearish?`,

    FIBONACCI: `
Focus on the VALIDATED OTE zone only (0.618-0.79 retracement — matches
A_Signal.js / JarvisSMC_Strategy.pine exactly). The old 0.15/0.75/0.84/0.90
levels were NEVER backtested and are no longer used for any live decision —
do not reference them.
Is price currently in the 0.618-0.79 OTE zone? On which timeframe?`,

    SESSION: `
CURRENT SESSION: ${currentSession?.name || 'Unknown'}
Focus on: Is this a high-probability session for this strategy?
London: 9:00–12:00 UTC | New York: 14:00–17:00 UTC | Asian: 02:00–06:00 UTC
Is this the AMD cycle (Accumulation, Manipulation, Distribution)?`,

    LIQUIDITY: `
Focus on: Where is liquidity resting above/below current price?
Equal highs, equal lows, prior swing highs/lows, round numbers.
Has liquidity been swept recently? Is price targeting liquidity?`,

    CONFLUENCE: `
PROVEN BEHAVIORS (top 5):
${behaviors?.slice(0, 5).map(b => `${b.title} — ${b.accuracy}% accuracy`).join('\n') || 'None yet'}
Focus on: How many conditions from this strategy are SIMULTANEOUSLY aligned?
Confluence score (out of 10)?`,

    MOMENTUM: `
Focus on: What is the current momentum direction?
Is price trending, ranging, or reversing? Is there a strong push or exhaustion?
Volume context and price velocity.`,

    PSYCHOLOGY: `
Focus on: Is this a psychologically safe setup?
Is the entry clear and not FOMO? Is the risk-reward logical?
Would a disciplined trader take this trade?`,

    INDIA: `
Focus on: NSE/BSE market context.
Nifty 50 / Bank Nifty direction. Sector performance.
Is this an Indian equity or F&O setup?`,

    GENERAL: `
Focus on the strategy conditions listed below.
Analyze each condition against current market.`,
  };

  const strategyConditions = `
THIS STRATEGY'S CONDITIONS:
${(strategy.conditions || []).map((c, i) => `${i + 1}. ${c}`).join('\n')}

ENTRY LOGIC: ${strategy.entry_logic || 'Not specified'}
SL LOGIC: ${strategy.sl_logic || 'Not specified'}
TP LOGIC: ${strategy.tp_logic || 'Not specified'}
SESSION FILTER: ${strategy.session_filter || 'Any'}
DAY FILTER: ${strategy.day_filter || 'Any'}
MIN CONFLUENCE: ${strategy.min_confluence || 6}/10
`;

  return `You are the JARVIS ${DNA_META[dna]?.label || dna} Agent — a specialized trading analyst.
You analyze XAU/USD market ONLY through the lens of this specific strategy.
Do NOT consider other strategies. Only your specialty matters here.
Return ONLY valid JSON. Be concise.

${baseContext}
${dnaSpecific[dna] || dnaSpecific.GENERAL}
${strategyConditions}

Analyze RIGHT NOW and return JSON. Do NOT include a numeric confidence
score — you have no way to calibrate what "73% confident" actually means,
and a made-up number is worse than none. Explain your reasoning in words
instead (key_conditions_met / conditions_missing already do this).
{
  "verdict": "BUY|SELL|WAIT",
  "entry_zone": "price range like 3320-3325 or null",
  "stop_loss": price_number_or_null,
  "target1": price_number_or_null,
  "target2": price_number_or_null,
  "key_conditions_met": ["condition1 is met because...", "condition2 is met because..."],
  "conditions_missing": ["condition X is NOT met because..."],
  "reason": "1-2 sentences explaining verdict",
  "risk_verdict": "CLEAR|CAUTION|AVOID"
}`;
}

// ── Single agent runner ───────────────────────────────────────────────────────

async function runSingleAgent(strategy, context) {
  const dna = detectStrategyDNA(strategy);
  const systemPrompt = buildAgentSystemPrompt(strategy, dna, context);

  try {
    const response = await fetch(apiUrl('/api/chat'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: systemPrompt,
        messages: [{
          role: 'user',
          content: `Analyze current market for strategy: "${strategy.title}". Return JSON only.`,
        }],
        max_tokens: 600,
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    const text = data.content?.[0]?.text || '';

    // Parse JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON in response');
    const parsed = JSON.parse(jsonMatch[0]);

    return {
      ...EMPTY_REPORT,
      agentId:           strategy.id,
      strategyName:      strategy.title,
      dna,
      agentWeight:       strategy.agentWeight,
      verdict:           parsed.verdict || 'WAIT',
      confidence:        null, // no longer requested from the agent — see prompt comment above
      winRate:           strategy.win_rate,
      totalTrades:       strategy.total_trades,
      entryZone:         parsed.entry_zone || null,
      stopLoss:          parsed.stop_loss || null,
      target1:           parsed.target1 || null,
      target2:           parsed.target2 || null,
      keyConditions:     parsed.key_conditions_met || [],
      missingConditions: parsed.conditions_missing || [],
      reason:            parsed.reason || '',
      riskVerdict:       parsed.risk_verdict || 'CLEAR',
      status:            'ok',
    };
  } catch (err) {
    return {
      ...EMPTY_REPORT,
      agentId:      strategy.id,
      strategyName: strategy.title,
      dna,
      agentWeight:  strategy.agentWeight,
      winRate:      strategy.win_rate,
      totalTrades:  strategy.total_trades,
      status:       'error',
      error:        err.message,
    };
  }
}

// ── Run ALL eligible agents (dynamic count — no fixed cap) ────────────────────
// Batches 5 at a time to stay within Groq free tier rate limits.
// onProgress(agentReport, index, total) is called after each agent completes.

export async function runAllAgents(context, onProgress) {
  const strategies = getAgentEligibleStrategies();
  if (strategies.length === 0) return [];

  const BATCH_SIZE = 5;
  const allReports = [];

  for (let i = 0; i < strategies.length; i += BATCH_SIZE) {
    const batch = strategies.slice(i, i + BATCH_SIZE);

    // Run this batch in parallel
    const batchReports = await Promise.allSettled(
      batch.map(strategy => runSingleAgent(strategy, context))
    );

    for (let j = 0; j < batchReports.length; j++) {
      const result = batchReports[j];
      const report = result.status === 'fulfilled'
        ? result.value
        : { ...EMPTY_REPORT, agentId: batch[j].id, strategyName: batch[j].title, status: 'error', error: result.reason?.message };

      allReports.push(report);

      if (onProgress) {
        onProgress(report, i + j, strategies.length);
      }
    }

    // Small pause between batches to respect rate limits
    if (i + BATCH_SIZE < strategies.length) {
      await new Promise(r => setTimeout(r, 300));
    }
  }

  return allReports;
}

// ── Get agent info without running (for UI display) ───────────────────────────

export function getAgentRoster() {
  return getAgentEligibleStrategies().map(s => ({
    id:          s.id,
    name:        s.title,
    dna:         detectStrategyDNA(s),
    status:      s.status,
    winRate:     s.win_rate,
    totalTrades: s.total_trades,
    weight:      s.agentWeight,
  }));
}
